// --------------------------------
// Copyright (c) 2023 Eco8 S.r.l.
// --------------------------------
// DatePicker.js

import React, { useState, useEffect } from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { Calendar, LocaleConfig } from "react-native-calendars";
import { MaterialIcons } from "@expo/vector-icons";
import colors from "../../../../../config/colors";
import DataPickerStyle from "../../../../../styles/Farmer/DataPickerStyle";
import { formatDate } from "../../../../../utils/utils";
import * as RNLocalize from "react-native-localize";
import fonts from "../../../../../config/fonts";
const customLocaleConfig = {
  de: {
    monthNames: [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ],
    monthNamesShort: [
      'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
      'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
    ],
    dayNames: [
      'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'
    ],
    dayNamesShort: [
      'So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'
    ],
    today: 'Heute'
  },
  it: {
    monthNames: [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ],
    monthNamesShort: [
      'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
      'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
    ],
    dayNames: [
      'Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'
    ],
    dayNamesShort: [
      'Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'
    ],
    today: 'Oggi'
  },
  en: {
    monthNames: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    monthNamesShort: [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ],
    dayNames: [
      'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
    ],
    dayNamesShort: [
      'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'
    ],
    today: 'Today'
  },
};

const CustomCalendarArrow = ({ direction, color }) => {
  return (
    <MaterialIcons
      name={direction === "left" ? "keyboard-arrow-left" : "keyboard-arrow-right"}
      size={24}
      color={color}
    />
  );
};

const DatePicker = ({ setCurrentDate, date, hasMax }) => {
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() - 1);
  const dayBefore = currentDate.toISOString().substring(0, 10);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().substring(0, 10));
  const [modalVisible, setModalVisible] = useState(false);
  const [buttonTitle, setButtonTitle] = useState(selectedDate);
  const [showSelectedDate, setShowSelectedDate] = useState(false);

  const userPreferredLanguage =
    RNLocalize.getLocales()[0].languageTag.split("-")[0];

  useEffect(() => {
    const locale = customLocaleConfig[userPreferredLanguage] || customLocaleConfig["en"];
    LocaleConfig.locales[userPreferredLanguage] = locale;
    LocaleConfig.defaultLocale = userPreferredLanguage;
    setButtonTitle(selectedDate);
  }, [userPreferredLanguage, selectedDate]);

  const onDateSelect = (date) => {
    setSelectedDate(date.dateString);
    setCurrentDate(date.dateString);
    setModalVisible(false);
  };

  const handlePress = () => {
    setModalVisible(true);
  };

  return (
    <View>
      <TouchableOpacity
        onPress={handlePress}
        style={[DataPickerStyle.button, { backgroundColor: colors.green }]}
      >
        <MaterialIcons name="event" size={24} color={colors.white} />
        <Text style={[DataPickerStyle.buttonText, { color: colors.white }]}>
          {formatDate(buttonTitle)}
        </Text>
      </TouchableOpacity>

      {modalVisible && (
        <View style={DataPickerStyle.modalContainer}>
          <View style={DataPickerStyle.modalBackground} />
          <TouchableOpacity
            activeOpacity={1}
            onPress={() => setModalVisible(false)}
            style={DataPickerStyle.modalOverlay}
          />
          <View style={DataPickerStyle.calendarContainer}>
            <Calendar
              current={selectedDate}
              minDate={dayBefore}
              maxDate={hasMax ? currentDate.toISOString().substring(0, 10) : null}
              onDayPress={onDateSelect}
              markedDates={{
                [new Date().toISOString().substring(0, 10)]: {
                  customStyles: {
                    container: {
                      backgroundColor: colors.green,
                      borderRadius: 8,
                    },
                    text: {
                      color: colors.white,
                      fontWeight: "bold",
                      fontFamily: fonts.regular,
                    },
                  },
                },
                [selectedDate]: {
                  customStyles: {
                    container: {
                      backgroundColor: colors.green,
                      borderRadius: 8,
                      fontSize: 20,
                    },
                    text: {
                      color: colors.white,
                      fontWeight: "bold",
                      fontSize: 20,
                      fontFamily: fonts.regular,
                    },
                  },
                },
              }}
              theme={{
                arrowColor: colors.green,
                todayBackgroundColor: colors.green,
                todayTextColor: colors.white,
                selectedDayBackgroundColor: colors.green,
                selectedDayTextColor: colors.white,
                arrowComponent: (props) => (
                  <CustomCalendarArrow {...props} color={colors.green} />
                ),
              }}
              renderArrow={(direction) => (
                <MaterialIcons
                  name={direction === "left" ? "keyboard-arrow-left" : "keyboard-arrow-right"}
                  size={24}
                  color={colors.green}
                />
              )}
            />
          </View>
        </View>
      )}
    </View>
  );
};

export default DatePicker;
